/**
 * מסחר והנחות — the money surface, and the dual control on it. OA-0002, 04 §13.2.1, 08 §8.4 / CF-66.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT A PANEL ON THE SUBSCRIPTION SCREEN
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 08 §8.4 / CF-66: two tenants identical in plan, SKUs, assignments and state and differing ONLY in a
 * discount must produce byte-identical access decisions — *"if changing the price invalidated the
 * entitlement cache, price would be part of entitlement"*. The subscription screen's own header names
 * a capacity view that displays a price as the first place those two get welded back together.
 *
 * So the discount routes were not added there. They are here, on their own address, and the only
 * thing that crosses between the two screens is a link. The separation is the control.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THREE ROUTES, AND THE THIRD ONE IS THE REASON THE FIRST TWO ARE SAFE
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `platform.discount.apply` applies up to the ordinary ceiling. `platform.discount.apply_full` goes
 * above it, up to and including a full waiver, and is a SEPARATE ROUTE because it is a separate
 * capability held by the platform owner alone — *"the ceiling is enforced by which route the caller
 * can reach"*. This screen therefore offers two distinct actions rather than one action with a
 * percentage box that silently changes meaning at 100.
 *
 * `platform.discount.approve` is the second approver. Without it `PENDING_SECOND_APPROVER` is a state
 * nothing can leave — a discount that was applied, recorded, and permanently unusable. Self-approval
 * is refused by the server against the LIVE approver count, and this screen never predicts that
 * refusal: it sends, and it renders the answer.
 *
 * ── WHAT IS DECLARED AND NEVER DEFAULTED ────────────────────────────────────────────────────────
 *
 * `isRelatedParty`. OA-0002 makes it a FALSIFIABLE ASSERTION attributable to a named requester, and a
 * field that defaults to `false` when omitted is a note rather than an assertion. The control is
 * therefore a required choice with two visible answers, not a checkbox that means "no" when nobody
 * touched it.
 *
 * ── AND THE AMOUNT IS A STRING ON THE WIRE ──────────────────────────────────────────────────────
 *
 * 0003: *money is fixed-point decimal, never binary floating point.* The server reads
 * `discountAmount` as a STRING for that reason, and the field here is a text control that is never
 * parsed into a number on the way out — a `Number()` in this file would defeat the rule at the first
 * `JSON.parse`.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { ServerRead } from '../components/ServerRead';
import { SkeletonRows, StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { listOf, useServerData } from '../api/useServerData';
import type { BenefitView, TenantAdminSummary } from '../api/contract';
import type { DiscountBody } from '../api/operations';
import { routes } from '../routes';

export interface PlatformCommercialScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'מסחר, הנחות ואישורים';

/**
 * ── THE FOUR SHAPES A BENEFIT CAN TAKE, AND THE FOURTH WAS UNREACHABLE. **2026-11-01.** ───────
 *
 * This comment read *"The three shapes a discount can take. A closed list; there is no fourth"*,
 * and it was true when it was written. Migration 0037 added one, on the Owner's own words:
 *
 *     *"מחיר קבוע מופחת לתקופה, כגון 50 ש״ח לחודש למשך חודשיים."*
 *
 * `CK_sd_kind` admits it, `CommercialService` validates it, `parseTerm` reads it and the billing
 * cycle applies it. **The only thing missing was this list**, so the capability existed
 * everywhere except where a person could ask for it — which is the shape of gap the Owner has
 * named twice: *"אל תציג יכולת מדומה כפעילה"*, and its converse.
 *
 * A REDUCED PRICE IS NOT A DISCOUNT, which is why it is a kind and not a number: a percentage is
 * a proportion of a price, `fixed_amount` is an amount taken OFF one, and this REPLACES it.
 */
const DISCOUNT_KINDS = ['percentage', 'fixed_amount', 'full_waiver', 'fixed_price'] as const;
type DiscountKind = (typeof DISCOUNT_KINDS)[number];

const DISCOUNT_KIND_HE: Readonly<Record<DiscountKind, string>> = {
  percentage: 'אחוז הנחה',
  fixed_amount: 'סכום קבוע',
  full_waiver: 'ויתור מלא על החיוב',
  fixed_price: 'מחיר מופחת קבוע לתקופה',
};

/**
 * ── WHICH KIND NEEDS AN END DATE. **0037, `CK_sd_fixed_price_has_end`.** ──────────────────────
 *
 * *"סיום ההטבה ומעבר למחיר הרגיל במועד הנכון."* A `fixed_price` with no end can never revert, so
 * it is not a benefit — it is a permanent price change wearing a benefit's name, and the table
 * refuses it. The other three are left open-ended on purpose: an open percentage or waiver is a
 * real arrangement this product permits.
 *
 * The form mirrors the constraint rather than restating a rule of its own: the field is required
 * for exactly the kind the database requires it for, and the server refuses the combination
 * either way.
 */
const END_REQUIRED: Readonly<Record<DiscountKind, boolean>> = {
  percentage: false,
  fixed_amount: false,
  full_waiver: false,
  fixed_price: true,
};

/**
 * THE KIND, IN WORDS, FOR A BENEFIT THAT WAS ALREADY GRANTED.
 *
 * Keyed by the string the server sends and NOT by `DiscountKind`: a row written before a kind was
 * retired, or by a release this one does not know about, must still render — as its own code,
 * which is information, rather than as a blank where a kind belongs.
 */
const BENEFIT_KIND_HE: Readonly<Record<string, string>> = {
  percentage: 'אחוז הנחה',
  fixed_amount: 'סכום קבוע',
  full_waiver: 'ויתור מלא על החיוב',
  fixed_price: 'מחיר מופחת קבוע לתקופה',
};

/**
 * WHAT THE BENEFIT IS WORTH, from whichever field its kind populates.
 *
 * `CK_sd_kind_value` admits exactly one populated shape per kind, so this reads the one the kind
 * names rather than the first field that happens to be non-null — which would print a reduction
 * as a replacement price on a row where both were somehow set.
 */
function benefitValueHe(benefit: BenefitView): string {
  switch (benefit.term.discountKind) {
    case 'percentage':
      return benefit.term.discountPct === null ? '—' : `${String(benefit.term.discountPct)}%`;
    case 'fixed_amount':
      return benefit.term.discountAmount ?? '—';
    case 'fixed_price':
      return benefit.term.fixedPriceAmount ?? '—';
    case 'full_waiver':
      return 'ללא חיוב';
    default:
      return '—';
  }
}

type Outcome =
  /**
   * THE UID TRAVELS WITH THE OUTCOME. **2026-11-01.**
   *
   * `platform.discount.apply` answers `{ outcome, discountUid }` and this screen kept only the
   * boolean, then told the operator *"the second approver types the decision's id into the form
   * on this screen"* — an id it had just been handed and thrown away. There is no reader for a
   * pending decision anywhere in this product, so the id was unrecoverable: the request existed,
   * waiting, and nobody could name it.
   */
  | {
      readonly kind: 'applied';
      readonly needsSecondApprover: boolean;
      readonly discountUid: string | null;
    }
  | { readonly kind: 'approved' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PlatformCommercialScreen({ tenantUid }: PlatformCommercialScreenProps): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return <PlatformCommercialBody tenantUid={tenantUid} />;
}

/**
 * THE BODY, AND WHY IT IS A SEPARATE COMPONENT.
 *
 * Every hook on this screen lives here, BELOW the surface guard, because `ctx.surface` is `null`
 * until `auth.whoami.read` answers (`shell/ContextProvider.tsx:181`) and every in-app navigation is
 * a full page load. Declared in one component with the guard above them, the early return would run
 * on the first (unresolved) render and the hooks on the second — React then sees two different hook
 * orders for one component and throws DURING RENDER, and the arrival is a blank page.
 *
 * It is a SPLIT and not a hoist. `useServerData` has no `enabled` option
 * (`api/useServerData.ts:73`), so lifting the reads above the guard would issue a PLATFORM read on
 * an unresolved or wrong-surface render — a denied request, and an audited write, for a screen the
 * caller is not on. Here nothing is read until the session has been stated as PLATFORM, and each
 * component's hook order is unconditional.
 */
function PlatformCommercialBody({ tenantUid }: PlatformCommercialScreenProps): JSX.Element {
  const [discountKind, setDiscountKind] = useState<DiscountKind>('percentage');
  const [discountPct, setDiscountPct] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [fixedPriceAmount, setFixedPriceAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [reason, setReason] = useState('');
  const [relatedParty, setRelatedParty] = useState<'yes' | 'no' | null>(null);
  const [approveUid, setApproveUid] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * ---- THE CUSTOMER IS READ FROM THE SERVER --------------------------------------------------
   *
   * THIS LINE USED TO BE:
   *
   *     const tenant = (fixturesEnabled() ? fixtureTenants : []).find(...) ?? null;
   *
   * In Production `fixturesEnabled()` is false, so the list was EMPTY, so `tenant` was always
   * `null`, so every real customer rendered "הלקוח אינו זמין" -- while the write buttons below it
   * were pointed at the real `tenantUid` from the address the whole time. A screen that cannot find
   * the customer it is about to act on is not a missing feature; it is a control panel whose labels
   * have come off.
   *
   * `platform.tenant.read` is the list every other control-plane screen already resolves a customer
   * from -- `PlatformTenantScreen` does exactly this -- so this adds no second authority and no
   * second answer about who the customers are.
   */
  const readTenants = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const tenantsRead = useServerData(readTenants);

  /*
   * ---- WHICH PRICE BOOK THIS CUSTOMER IS ON. **F1R-04.** ---------------------------------------
   *
   * `subscription.price_book_id` gave a pinned book priority over the published one, and no
   * operation anywhere set it — so §4.2.4's grandfathered agreement was a column a DBA could fill
   * in, which is the same as not having the mechanism.
   *
   * It lives on THIS screen and not on the capacity screen for the reason this screen's header
   * gives: what a customer is charged is money, and money is deliberately kept away from
   * entitlement. Pinning a book is the most consequential money decision in the product.
   */
  const readPin = useCallback(
    () =>
      callOperation('platform.subscription.price_book.read', {
        params: { tenant_uid: tenantUid },
      }),
    [tenantUid],
  );
  const pinRead = useServerData(readPin);

  const readBooks = useCallback(() => callOperation('platform.price_book.read', {}), []);
  const booksRead = useServerData(readBooks);

  /*
   * ── WHAT THIS CUSTOMER ALREADY HAS. **2026-11-01.** ───────────────────────────────────────
   *
   * This screen could grant a benefit eleven ways and show none. Applying a second discount over
   * a live one is a decision somebody makes with the first one in front of them, and a request
   * waiting for its second approver was worse still: its uid travelled in one response, to one
   * screen, and nothing read the table back — so a pending decision made yesterday could not be
   * named today.
   *
   * RE-READ AFTER A WRITE, the same way `pinRead` is: a list that still shows yesterday's
   * benefits after the operator granted one is a screen that teaches people not to trust it.
   */
  const readBenefits = useCallback(
    () => callOperation('platform.discount.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const benefitsRead = useServerData(readBenefits);
  const tenant =
    listOf<TenantAdminSummary, 'tenants'>(tenantsRead.result, 'tenants').find(
      (t) => t.tenantUid === tenantUid,
    ) ?? null;
  const back = { labelHe: 'הלקוחות', href: routes.platformTenants };

  /*
   * LOADING, FAILED AND NOT-FOUND ARE THREE STATES AND ARE RENDERED AS THREE.
   *
   * The old branch had one: `tenant === null` meant "unavailable", which covered a read still in
   * flight, a read the gate refused, an outage, and a uid that names nobody -- four situations
   * needing four different actions from the operator.
   */
  if (tenantsRead.result.state === 'loading') {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <SkeletonRows rows={4} />
      </AppShell>
    );
  }

  if (tenantsRead.result.state === 'failed') {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <ActionFailureNotice failure={tenantsRead.result.failure} />
      </AppShell>
    );
  }

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="הלקוח אינו נמצא"
          bodyHe="הקריאה לשרת הצליחה, ואין ברשימת הלקוחות לקוח בכתובת הזו."
          tone="unavailable"
          nextStep={
            <a className="btn btn--nav" href={routes.platformTenants}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  /** The pin, submitted. F1R-04. Same shape as `applyDiscount`: send, report, re-read. */
  async function savePriceBook(priceBookUid: string | null, pinReason: string): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      await callOperation('platform.subscription.price_book.set', {
        params: { tenant_uid: tenantUid },
        body: { priceBookUid, reason: pinReason },
      });
      pinRead.reload();
    } catch (error: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(error) });
    } finally {
      setSending(false);
    }
  }

  async function applyDiscount(aboveCeiling: boolean): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // ONE BODY, TWO OPERATIONS — and the two calls are written out rather than selected by a
      // string. `apply_full` is a SEPARATE ACTION reserved to the platform owner and requiring a
      // second approver, not the same action with a larger number, and a path assembled as
      // `discounts${aboveCeiling ? '/full' : ''}` said that in a way no reader and no scanner could
      // see. Spelled out, each action is individually visible to the reachability scan: deleting
      // either control fails CI on its own rather than hiding behind the other.
      const body: DiscountBody = {
        discountKind,
        // A percentage is a number; an AMOUNT is a string and stays one. 0003.
        discountPct: discountPct === '' ? null : Number(discountPct),
        discountAmount: discountAmount === '' ? null : discountAmount,
        fixedPriceAmount: fixedPriceAmount === '' ? null : fixedPriceAmount,
        currencyCode: null,
        scope: 'subscription',
        scopeSkuCode: null,
        effectiveFrom,
        /*
         * ── IT WAS A HARD-CODED `null`, AND THAT WAS THE WHOLE OF GAP 2. **2026-11-01.** ───────
         *
         * *"סיום ההטבה ומעבר למחיר הרגיל במועד הנכון."* `effective_to` has existed since 0003,
         * `priceCycle` asks which benefit is in force on each DAY, and 0037 made an end
         * MANDATORY for a reduced price. This screen sent `null` for every benefit it applied —
         * so every one of them ran for ever, and a `fixed_price` could not be applied at all.
         */
        effectiveTo: effectiveTo === '' ? null : effectiveTo,
        reason,
        // DECLARED, never defaulted. OA-0002.
        isRelatedParty: relatedParty === 'yes',
      };
      const params = { tenant_uid: tenantUid };
      const result = aboveCeiling
        ? await callOperation('platform.discount.apply_full', { params, body })
        : await callOperation('platform.discount.apply', { params, body });
      setOutcome({
        kind: 'applied',
        needsSecondApprover: result?.outcome === 'PENDING_SECOND_APPROVER',
        discountUid: result?.discountUid ?? null,
      });
      benefitsRead.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  /**
   * WHAT IS STILL MISSING BEFORE EITHER BUTTON MEANS ANYTHING.
   *
   * It is one expression rather than two copies on two buttons, which is how the end date would
   * otherwise have reached one of them and not the other. It mirrors the constraints the table
   * enforces; the server refuses the same combinations, and this is so a person is not told so
   * after typing a reason.
   */
  const incomplete =
    reason === '' ||
    relatedParty === null ||
    effectiveFrom === '' ||
    (END_REQUIRED[discountKind] && effectiveTo === '') ||
    (discountKind === 'fixed_price' && fixedPriceAmount === '') ||
    (discountKind === 'percentage' && discountPct === '') ||
    (discountKind === 'fixed_amount' && discountAmount === '');

  async function approve(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    try {
      await callOperation('platform.discount.approve', {
        params: { discount_uid: approveUid },
        body: {},
      });
      setOutcome({ kind: 'approved' });
      benefitsRead.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'כרטיס הלקוח', href: routes.platformTenant(tenant.tenantUid) }}
    >
      <section className="notice" data-tone="info">
        <h2 className="notice__title">{`החלטות מסחריות עבור ${tenant.legalName}`}</h2>
        <p className="notice__body">
          הנחה אינה משפיעה על ההרשאות ועל המכסות של הלקוח. שני לקוחות זהים שנבדלים בהנחה בלבד מקבלים
          בדיוק אותן החלטות גישה, ולכן ההנחות נמצאות במסך נפרד ממסך המכסות.
        </p>
        <p className="notice__body">
          הנחה מעל התקרה הרגילה היא פעולה נפרדת, ולא אותה פעולה עם מספר גדול יותר. היא שמורה לבעלים
          של הפלטפורמה ודורשת אישור של אדם שני.
        </p>
        <div className="actions-row">
          <a className="btn" href={routes.platformTenantSubscription(tenant.tenantUid)}>
            מנוי ומכסות
          </a>
        </div>
      </section>

      {/*
        THE NEGOTIATED PRICE BOOK. F1R-04.
        Radio buttons and not a dropdown: 09 §3.4 keeps every selector in the shell, and the choice
        here is short enough to be visible without opening anything. "המחירון הרשמי" is the FIRST
        option because returning a customer to the list price is the ordinary act; pinning is the
        exception.
      */}
      <ServerRead result={pinRead.result} whatHe="את ספר המחירים של הלקוח" skeletonRows={2}>
        {(pin) => (
          <section className="form" aria-label="ספר מחירים ללקוח">
            <h2 className="form__title">ספר המחירים של הלקוח</h2>
            <p className="tile__lead">
              {/*
                `== null` AND NOT `=== null`: the contract says the field is a book or null, and a
                response missing the key entirely is malformed. "No pin" is the safe reading of
                both, and one absent key must not blank a screen an operator is mid-decision on.
              */}
              {pin.pinned == null
                ? 'הלקוח מתומחר לפי המחירון הרשמי. כל שינוי במחירון הרשמי יחול גם עליו.'
                : `הלקוח מתומחר לפי הסכם נפרד: ${pin.pinned.code}. פרסום מחירון רשמי חדש לא ישנה את המחירים שלו.`}
            </p>
            <p className="tile__lead">
              שינוי כאן משפיע על מכירות עתידיות בלבד. מחירים שכבר נשמרו בשורות המנוי אינם משתנים
              לעולם — זו כל מטרת השמירה שלהם.
            </p>

            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                const form = new FormData(e.target as HTMLFormElement);
                const chosen = String(form.get('priceBookChoice') ?? '');
                // The empty choice is the OFFICIAL list, and it travels as an explicit null.
                void savePriceBook(
                  chosen === '' ? null : chosen,
                  String(form.get('priceBookReason') ?? ''),
                );
              }}
            >
              <fieldset className="field" aria-label="בחירת ספר מחירים">
                <label className="choice">
                  <input
                    type="radio"
                    name="priceBookChoice"
                    value=""
                    defaultChecked={pin.pinned == null}
                  />
                  <span>המחירון הרשמי</span>
                </label>
                {(booksRead.result.state === 'loaded' ? booksRead.result.data.priceBooks : [])
                  .filter((b) => b.status === 'active')
                  .map((b) => (
                    <label key={b.priceBookUid} className="choice">
                      <input
                        type="radio"
                        name="priceBookChoice"
                        value={b.priceBookUid}
                        defaultChecked={pin.pinned?.priceBookUid === b.priceBookUid}
                      />
                      <span>{b.displayNameHe}</span>
                      <span dir="ltr" className="company-cell__id">
                        {b.code}
                      </span>
                    </label>
                  ))}
              </fieldset>

              <label className="field">
                <span className="field__label">סיבת השינוי</span>
                <input className="field__input" name="priceBookReason" minLength={3} required />
              </label>

              <div className="actions-row">
                <button type="submit" className="btn btn--primary" disabled={sending}>
                  שמירת ספר המחירים
                </button>
              </div>
            </form>
          </section>
        )}
      </ServerRead>

      {outcome === null ? null : outcome.kind === 'applied' ? (
        <StateBlock
          titleHe={outcome.needsSecondApprover ? 'ההנחה נרשמה וממתינה לאישור שני' : 'ההנחה נרשמה'}
          bodyHe={
            outcome.needsSecondApprover
              ? 'ההחלטה נרשמה ואינה בתוקף עד שאדם שני יאשר אותה. אישור עצמי אינו אפשרי.'
              : 'ההחלטה נרשמה ביומן יחד עם הסיבה, עם ההצהרה על צד קשור ועם מי שביצע אותה.'
          }
          nextStep={
            outcome.needsSecondApprover ? (
              <>
                <p>המאשר השני מזין את מזהה ההחלטה בטופס האישור שבמסך הזה.</p>
                {/*
                  ── AND HERE IS THE ID, WHICH THIS SCREEN USED TO THROW AWAY. ────────────────

                  The route answers with `discountUid` and the screen kept only *"a second
                  approver is needed"*, then told the operator to type an id it had just been
                  handed and discarded. There is no reader for a pending decision anywhere in
                  this product, so the id was unrecoverable and the request sat waiting with
                  nobody able to name it.
                */}
                {outcome.discountUid === null ? null : (
                  <p className="field__hint" dir="ltr">
                    {outcome.discountUid}
                  </p>
                )}
              </>
            ) : undefined
          }
        />
      ) : outcome.kind === 'approved' ? (
        <StateBlock
          titleHe="ההחלטה אושרה"
          bodyHe="האישור נרשם יחד עם המאשר. ההחלטה יצאה ממצב ההמתנה."
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOutcome(null)}
        />
      )}

      {/*
        ── WHAT IS IN FORCE, AND WHAT IS WAITING. **2026-11-01.** ───────────────────────────────

        ABOVE the form that grants another one, because that is the order of the decision: an
        operator about to discount a customer needs to know what that customer already has. A
        panel below the form would be read after the act it exists to inform.
      */}
      <ServerRead result={benefitsRead.result} whatHe="את ההטבות של הלקוח" skeletonRows={2}>
        {(data) => {
          /*
            `?? []` AND NOT `data.benefits`. The contract says the key is always there and a
            response without it is malformed -- which is exactly the case that must not blank a
            screen an operator is mid-decision on. It is the same reading the price-book panel
            above gives to its own absent key, and a case in this file's suite reaches it: the
            read and the two writes share one address, so a harness answering both with one body
            hands this branch a shape with no list in it.
          */
          const benefits = data.benefits ?? [];
          return (
          <section className="form" aria-label="הטבות הלקוח">
            <h2 className="form__title">הטבות קיימות</h2>
            {benefits.length === 0 ? (
              /*
                A READ THAT SUCCEEDED AND FOUND NONE. It is a sentence and not an empty table,
                and it says which of the four states this is: no benefit, rather than no read.
              */
              <p className="tile__lead">לא נרשמה ללקוח הזה הטבה בתוקף ואין בקשה הממתינה לאישור.</p>
            ) : (
              <ul className="plain-list">
                {benefits.map((benefit) => (
                  <li key={benefit.discountUid} className="tile" data-benefit-status={benefit.status}>
                    <p className="tile__lead">
                      <strong>{BENEFIT_KIND_HE[benefit.term.discountKind] ?? benefit.term.discountKind}</strong>
                      {' · '}
                      {benefitValueHe(benefit)}
                    </p>
                    <p className="tile__lead">
                      {/*
                        THE DATES, AND AN OPEN END SAID IN WORDS. An empty cell where a date
                        belongs reads as a missing value; *"ללא מועד סיום"* is the fact.
                      */}
                      {`מ-${benefit.term.effectiveFrom}`}
                      {benefit.term.effectiveTo === null
                        ? ' · ללא מועד סיום'
                        : ` · עד ${benefit.term.effectiveTo}`}
                    </p>
                    <p className="tile__lead">{`הסיבה שנרשמה: ${benefit.reasonHe}`}</p>
                    {benefit.isRelatedParty ? <p className="tile__lead">הוצהר כצד קשור.</p> : null}
                    {benefit.status === 'pending_approval' ? (
                      <>
                        <p className="tile__lead">
                          <strong>ממתינה לאישור של אדם שני.</strong> אינה בתוקף עד שתאושר.
                        </p>
                        {/*
                          THE UID, ON THE SCREEN. It is what the approval form below takes, and
                          until this read existed it was unrecoverable once the session that
                          created the request had gone.
                        */}
                        <p className="field__hint" dir="ltr">
                          {benefit.discountUid}
                        </p>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
          );
        }}
      </ServerRead>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void applyDiscount(false);
        }}
      >
        <h2 className="form__title">מתן הנחה</h2>

        <fieldset className="field field--choice">
          <legend className="field__label">סוג ההנחה</legend>
          {DISCOUNT_KINDS.map((kind) => (
            <label key={kind} className="choice">
              <input
                type="radio"
                name="discountKind"
                value={kind}
                checked={discountKind === kind}
                onChange={() => setDiscountKind(kind)}
              />
              <span>{DISCOUNT_KIND_HE[kind]}</span>
            </label>
          ))}
        </fieldset>

        <label className="field">
          <span className="field__label">אחוז ההנחה</span>
          <input
            className="field__input"
            name="discountPct"
            dir="ltr"
            inputMode="numeric"
            value={discountPct}
            disabled={discountKind !== 'percentage'}
            onChange={(e) => setDiscountPct(e.currentTarget.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">סכום ההנחה</span>
          <input
            className="field__input"
            name="discountAmount"
            dir="ltr"
            value={discountAmount}
            disabled={discountKind !== 'fixed_amount'}
            onChange={(e) => setDiscountAmount(e.currentTarget.value)}
          />
          <span className="field__hint">
            הסכום נשמר כפי שהוזן, ספרה בספרה.
          </span>
        </label>

        {/*
          ── THE REDUCED PRICE. 0037, AND THE FORM HAD NO FIELD FOR IT. ────────────────────────

          *"מחיר קבוע מופחת לתקופה, כגון 50 ש״ח לחודש למשך חודשיים."* It is its own column and
          its own kind, because a replacement price is not a reduction: anything that totals what
          a customer saved would add a REPLACEMENT as though it were a discount.
        */}
        <label className="field">
          <span className="field__label">המחיר המופחת לתקופה</span>
          <input
            className="field__input"
            name="fixedPriceAmount"
            dir="ltr"
            value={fixedPriceAmount}
            disabled={discountKind !== 'fixed_price'}
            onChange={(e) => setFixedPriceAmount(e.currentTarget.value)}
          />
          <span className="field__hint">
            המחיר שיחול במקום מחיר המחירון, לכל תקופת חיוב, עד תום ההטבה.
          </span>
        </label>

        <label className="field">
          <span className="field__label">מועד תחילת ההנחה</span>
          <input
            className="field__input"
            name="effectiveFrom"
            dir="ltr"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
          />
        </label>

        {/*
          ── THE END OF THE BENEFIT, WHICH THIS FORM SENT AS `null` EVERY TIME. ────────────────

          *"סיום ההטבה ומעבר למחיר הרגיל במועד הנכון."* `effective_to` has existed since 0003 and
          the billing cycle asks, for every DAY of a period, which benefit is in force on it — so
          the end needed no job, only a date. The form never collected one, so every benefit
          granted here ran for ever and a reduced price could not be granted at all.

          REQUIRED FOR A REDUCED PRICE AND OPTIONAL OTHERWISE, which is `CK_sd_fixed_price_has_end`
          exactly. The hint says which of the two the person is looking at rather than leaving
          them to discover it from a refusal.
        */}
        <label className="field">
          <span className="field__label">
            {END_REQUIRED[discountKind] ? 'מועד סיום ההטבה' : 'מועד סיום ההטבה (לא חובה)'}
          </span>
          <input
            className="field__input"
            name="effectiveTo"
            dir="ltr"
            value={effectiveTo}
            required={END_REQUIRED[discountKind]}
            onChange={(e) => setEffectiveTo(e.currentTarget.value)}
          />
          <span className="field__hint">
            {END_REQUIRED[discountKind]
              ? 'מחיר מופחת חייב מועד סיום. בלעדיו אין חזרה למחיר הרגיל, וזהו שינוי מחיר קבוע ולא הטבה.'
              : 'בתום המועד חוזר הלקוח למחיר הרגיל. ריק פירושו הטבה ללא מועד סיום.'}
          </span>
        </label>

        {/*
          A REQUIRED CHOICE WITH TWO VISIBLE ANSWERS, not a checkbox. OA-0002 makes this a falsifiable
          assertion by a named requester; a control that means "no" when nobody touched it turns the
          assertion back into a note.
        */}
        <fieldset className="field field--choice">
          <legend className="field__label">האם הלקוח הוא צד קשור</legend>
          <label className="choice">
            <input
              type="radio"
              name="isRelatedParty"
              value="no"
              checked={relatedParty === 'no'}
              onChange={() => setRelatedParty('no')}
            />
            <span>אינו צד קשור</span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="isRelatedParty"
              value="yes"
              checked={relatedParty === 'yes'}
              onChange={() => setRelatedParty('yes')}
            />
            <span>צד קשור</span>
          </label>
          <span className="field__hint">
            ההצהרה נרשמת על שמך.
          </span>
        </fieldset>

        <label className="field">
          <span className="field__label">סיבת ההנחה</span>
          <input
            className="field__input"
            name="discount-reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </label>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || incomplete}
          >
            מתן הנחה בתוך התקרה
          </button>
          <button
            type="button"
            className="btn btn--irreversible"
            disabled={sending || incomplete}
            onClick={() => void applyDiscount(true)}
          >
            מתן הנחה מעל התקרה
          </button>
        </div>
        <p className="form__note">
          שתי הפעולות נפרדות בכוונה. מי שאינו רשאי לחרוג מהתקרה יקבל סירוב על הפעולה השנייה, ולא
          יגלה זאת רק אחרי שהזין מספר גדול.
        </p>
      </form>

      <form className="form" onSubmit={(e) => void approve(e)}>
        <h2 className="form__title">אישור החלטה שממתינה למאשר שני</h2>
        <label className="field">
          <span className="field__label">מזהה ההחלטה</span>
          <input
            className="field__input"
            name="discountUid"
            dir="ltr"
            value={approveUid}
            onChange={(e) => setApproveUid(e.currentTarget.value)}
          />
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || approveUid === ''}>
            אישור ההחלטה
          </button>
        </div>
      </form>
    </AppShell>
  );
}
